function networkConfig() {
  return window.OnScreenGamesNetworkConfig?.get() || {
    signalingUrl: `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws/`,
    iceServers: [{ urls: "stun:stun1.l.google.com:19302" }],
  };
}

function signalingUrl(ticket) {
  const configured = process.env.REACT_APP_TOURNAMENT_SIGNALING_SERVER;
  const url = new URL(configured || networkConfig().signalingUrl, window.location.href);
  if (!configured) {
    const basePath = url.pathname.replace(/\/+$/, "");
    url.pathname = basePath.endsWith("/ws")
      ? `${basePath}/tournament/`
      : "/ws/tournament/";
  }
  url.searchParams.set("ticket", ticket);
  return url.toString();
}

export default class TournamentHostTransport {
  constructor({ ticketProvider, onInput, onConnectionChange }) {
    this.ticketProvider = ticketProvider;
    this.onInput = onInput;
    this.onConnectionChange = onConnectionChange;
    this.socket = null;
    this.peers = new Map();
    this.closed = false;
    this.reconnectTimer = null;
  }

  async connect() {
    if (this.closed) return;
    await window.OnScreenGamesNetworkConfig?.ready;
    if (this.closed) return;
    const ticket = await this.ticketProvider();
    const socket = new WebSocket(signalingUrl(ticket));
    this.socket = socket;
    socket.onmessage = (event) => this.handleSignal(JSON.parse(event.data));
    socket.onerror = (error) => console.warn("Tournament signaling error", error);
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      if (!this.closed) {
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = window.setTimeout(() => this.connect().catch(console.error), 2000);
      }
    };
  }

  sendSignal(message) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  async handleSignal(message) {
    if (message.type === "controller.disconnected") {
      this.onConnectionChange?.(message.controllerId, false);
      return;
    }
    if (message.type === "controller.connected") {
      this.onConnectionChange?.(message.controllerId, true);
      return;
    }
    const controllerId = message.controllerId;
    if (!controllerId) return;
    let peer = this.peers.get(controllerId);
    if (message.offer) {
      peer?.connection.close();
      peer = this.createPeer(controllerId);
      await peer.connection.setRemoteDescription(message.offer);
      for (const candidate of peer.pendingCandidates.splice(0)) {
        await peer.connection.addIceCandidate(candidate);
      }
      const answer = await peer.connection.createAnswer();
      await peer.connection.setLocalDescription(answer);
      this.sendSignal({ controllerId, answer: peer.connection.localDescription });
      return;
    }
    if (message.candidate) {
      if (!peer) peer = this.createPeer(controllerId);
      if (peer.connection.remoteDescription) await peer.connection.addIceCandidate(message.candidate);
      else peer.pendingCandidates.push(message.candidate);
    }
  }

  createPeer(controllerId) {
    const connection = new RTCPeerConnection({ iceServers: networkConfig().iceServers });
    const peer = { connection, channel: null, pendingCandidates: [] };
    this.peers.set(controllerId, peer);
    connection.onicecandidate = (event) => {
      if (event.candidate) this.sendSignal({ controllerId, candidate: event.candidate });
    };
    connection.ondatachannel = (event) => {
      peer.channel = event.channel;
      peer.channel.onopen = () => this.onConnectionChange?.(controllerId, true);
      peer.channel.onclose = () => this.onConnectionChange?.(controllerId, false);
      peer.channel.onmessage = (dataEvent) => {
        try {
          this.onInput?.(controllerId, JSON.parse(dataEvent.data));
        } catch (error) {
          console.warn("Invalid tournament controller message", error);
        }
      };
    };
    connection.onconnectionstatechange = () => {
      if (["failed", "closed"].includes(connection.connectionState)) {
        this.onConnectionChange?.(controllerId, false);
      }
    };
    return peer;
  }

  send(controllerId, message) {
    const channel = this.peers.get(controllerId)?.channel;
    if (channel?.readyState !== "open") return false;
    channel.send(JSON.stringify(message));
    return true;
  }

  broadcast(message) {
    for (const controllerId of this.peers.keys()) this.send(controllerId, message);
  }

  close() {
    this.closed = true;
    window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
    for (const peer of this.peers.values()) peer.connection.close();
    this.peers.clear();
  }
}
